/** Browser events RC uses to drive in-page UI (no MainAppPanel imports). */

export const RC_OPEN_LINE_ITEM_EVENT = "rc:open-line-item";
export const RC_REFRESH_RFQ_EVENT = "rc:refresh-rfq";
export const RC_OPEN_CLIENT_PRICE_QUOTE_EVENT = "rc:open-client-price-quote";
export const RC_OPEN_THANK_YOU_LETTER_EVENT = "rc:open-thank-you-letter";

/** Cross-tab signal so opener can show thank-you after PO approve in a new tab. */
const THANK_YOU_LETTER_STORAGE_KEY = "procurement:open-thank-you-letter";
const THANK_YOU_LETTER_PENDING_KEY = "procurement:pending-thank-you-letter";
const THANK_YOU_LETTER_PENDING_MS = 2 * 60 * 1000;

export type RcOpenLineItemDetail = {
  homeworkItemId: number;
  rfqId?: number;
};

export type RcRefreshRfqDetail = {
  rfqId: number;
};

export type RcOpenClientPriceQuoteDetail = {
  rfqId: number;
};

export type RcOpenThankYouLetterDetail = {
  rfqId: number;
  poNumber?: string;
};

export function dispatchOpenLineItem(detail: RcOpenLineItemDetail): void {
  if (typeof window === "undefined") return;
  window.dispatchEvent(
    new CustomEvent(RC_OPEN_LINE_ITEM_EVENT, { detail })
  );
}

export function dispatchRefreshRfq(rfqId: number): void {
  if (typeof window === "undefined" || !rfqId) return;
  window.dispatchEvent(
    new CustomEvent(RC_REFRESH_RFQ_EVENT, { detail: { rfqId } })
  );
}

export function dispatchOpenClientPriceQuote(rfqId: number): void {
  if (typeof window === "undefined" || !rfqId) return;
  window.dispatchEvent(
    new CustomEvent(RC_OPEN_CLIENT_PRICE_QUOTE_EVENT, {
      detail: { rfqId },
    })
  );
}

/** Open thank-you letter in this tab and notify other tabs (PO approve popup). */
export function dispatchOpenThankYouLetter(
  detail: RcOpenThankYouLetterDetail
): void {
  if (typeof window === "undefined" || !detail.rfqId) return;
  const payload = { ...detail, t: Date.now() };
  window.dispatchEvent(
    new CustomEvent(RC_OPEN_THANK_YOU_LETTER_EVENT, { detail })
  );
  try {
    localStorage.setItem(THANK_YOU_LETTER_STORAGE_KEY, JSON.stringify(payload));
  } catch {
    /* ignore quota / private mode */
  }
  try {
    sessionStorage.setItem(THANK_YOU_LETTER_PENDING_KEY, JSON.stringify(payload));
  } catch {
    /* ignore */
  }
}

/** Consume a recently requested thank-you letter for this RFQ (if any). */
export function consumePendingThankYouLetter(
  rfqId: number
): RcOpenThankYouLetterDetail | null {
  if (typeof window === "undefined" || !rfqId) return null;

  const tryParse = (
    raw: string | null,
    clear: () => void
  ): RcOpenThankYouLetterDetail | null => {
    if (!raw) return null;
    try {
      const parsed = JSON.parse(raw) as RcOpenThankYouLetterDetail & {
        t?: number;
      };
      if (parsed?.rfqId !== rfqId) return null;
      const age = Date.now() - (parsed.t ?? 0);
      if (age < 0 || age > THANK_YOU_LETTER_PENDING_MS) {
        clear();
        return null;
      }
      clear();
      return { rfqId: parsed.rfqId, poNumber: parsed.poNumber };
    } catch {
      return null;
    }
  };

  const fromSession = tryParse(
    sessionStorage.getItem(THANK_YOU_LETTER_PENDING_KEY),
    () => sessionStorage.removeItem(THANK_YOU_LETTER_PENDING_KEY)
  );
  if (fromSession) return fromSession;

  // Cross-tab: review tab wrote localStorage; opener may open RFQ later.
  return tryParse(localStorage.getItem(THANK_YOU_LETTER_STORAGE_KEY), () => {
    try {
      localStorage.removeItem(THANK_YOU_LETTER_STORAGE_KEY);
    } catch {
      /* ignore */
    }
  });
}

/** Subscribe to thank-you letter open requests (same tab + other tabs). */
export function subscribeOpenThankYouLetter(
  onOpen: (detail: RcOpenThankYouLetterDetail) => void
): () => void {
  const onEvent = (event: Event) => {
    const detail = (event as CustomEvent<RcOpenThankYouLetterDetail>).detail;
    if (detail?.rfqId) onOpen(detail);
  };
  const onStorage = (event: StorageEvent) => {
    if (event.key !== THANK_YOU_LETTER_STORAGE_KEY || !event.newValue) return;
    try {
      const parsed = JSON.parse(event.newValue) as RcOpenThankYouLetterDetail;
      if (parsed?.rfqId) onOpen(parsed);
    } catch {
      /* ignore bad payload */
    }
  };
  window.addEventListener(RC_OPEN_THANK_YOU_LETTER_EVENT, onEvent);
  window.addEventListener("storage", onStorage);
  return () => {
    window.removeEventListener(RC_OPEN_THANK_YOU_LETTER_EVENT, onEvent);
    window.removeEventListener("storage", onStorage);
  };
}
